import { useLocalStorageState } from 'ahooks';
import { isCancel } from 'axios';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { retainLegacyChat } from 'src/utils/legacyDecrypt';
import { useBlobUrlHolder } from 'src/hooks/useBlobUrlHolder.hook';
import { useTranslation } from 'react-i18next';
import { NIL, v4 as uuid } from 'uuid';
import { PipeService } from '../../pages/Pipe/Pipe.service.ts';
import {
  tFunnelsWithStages,
  tRequestChat,
} from '../../pages/Pipe/Pipe.types.ts';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context.ts';
import { useSocketsContext } from '../../providers/SocketsProvider/Sockets.context.ts';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums.ts';
import { tTemplates } from '../../providers/SocketsProvider/Sockets.types.ts';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context.ts';
import { useLeadAgentPaused } from './components/MessageBar/useLeadAgentPaused.hook';
import { wasFunnelRecentlyEvicted } from '../../providers/FunnelCacheProvider/recentlyEvictedFunnels.ts';
import {
  clearFunnelChecked,
  markFunnelChecked,
  wasFunnelChecked,
} from '../../providers/FunnelCacheProvider/staleFunnelsChecked.ts';
import { tSentTemplateCounter } from '../../pages/WhatsappApi/components/TemplatesTable/SentTemplateCounter.types.ts';
import { tUndeliveredMessage } from '../../pages/WhatsappApi/components/TemplatesTable/UndeliveredMessage.types.ts';
import {
  tConversationSummary,
  tCreateSummaryAIRequest,
  tSummaryEligibilityResponse,
  tSummaryError,
} from '../../types/summary.types.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import {
  filterRecentNonDeletedMessages,
  findMostRecentSummary,
  filterMessagesAfterDate,
} from '../../utils/messageFilters.ts';
import { normalizeLeadTags } from '../AdvanceSearch/AdvanceSearchModal.functions.ts';
import { tAdvanceSearchLead } from '../AdvanceSearch/AdvanceSearchModal.types.ts';
import useChatActions from './Chat.actions.ts';
import { ChatContext } from './Chat.constants.ts';

// front.chat.switch.duration is a perf-timing event that fires on every chat
// switch; unsampled it dominated the project's event volume. Events carry
// sample_rate so volume readings can be scaled back up.
const CHAT_SWITCH_DURATION_SAMPLE_RATE = 0.1;
import {
  ChatMessageContainerEnum,
  ChatState,
  ChatTextAreaContainerEnum,
  EventType,
} from './Chat.enum.ts';
import { ChatService } from './Chat.service.ts';
import {
  tChatAttachment,
  tChatContext,
  tChatEvent,
  tChatEvents,
  tChatSwitchDurationEvent,
  tCurrentChat,
  tFreeEntryPointData,
  tFunnelInfo,
  tLatestChatPage,
  tLeadInfo,
  tParticipant,
  tQuickReply,
  tSelectedFollowUp,
  tSelectedNote,
  tTakeConversationRequest,
  tTemplatePickerRequest,
} from './Chat.types.ts';
import { tComposerSealQuoteScope } from '../../pages/Wallet/Wallet.types.ts';
import { ChatInfoEnum } from './components/ChatInfo/ChatInfo.enum.ts';
import { tQuickActionState } from './components/MessageBar/MessageBar.types.ts';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context.ts';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums.ts';
import {
  classifyChatSwitchCacheClass,
  getNetworkEffectiveType,
  mergeChatEventsByEventId,
  unwrapTolerantSidePanelResults,
} from './Chat.functions.ts';
import {
  resolveProbeIntegration,
  useWhatsAppWebConnection,
} from './useWhatsAppWebConnection.hook.ts';
import {
  resolveWapiActivation,
  tWapiActivation,
} from 'src/pages/WhatsappApi/hooks/useWapiNotConnected';

// Session-scoped flag: ensure the "WAPI desconectado" info toast appears at
// most once per browser session. Cross-tab is fine — each tab informs the
// user once. Tracked here (module scope) instead of state because the toast
// is fired from inside an async data-load path that re-runs on every chat
// switch; keeping it in component state would re-render unnecessarily.
const WAPI_FALLBACK_TOAST_KEY = 'bug336:wapi-fallback-shown';

type tWapiFallbackToast = {
  /** sessionStorage flag: each fact is told once per session. */
  sessionKey: string;
  type: eAlertType;
  messageKey: string;
  /** Offers the connect modal (roles that can run it only). */
  offersReconnect: boolean;
};

// INT-1516 KTD10 — a held or failed WhatsApp API record falls back to WhatsApp
// Web like a disconnected one (it is not CONNECTED), but "está desconectado"
// is wrong for both: held informs, failed asks for the reconnect and carries
// it. Each verdict has its own session flag, so a customer told "Meta is
// confirming" still hears "reconnect" when the record fails later in the same
// session. The disconnected toast keeps its original flag.
const WAPI_FALLBACK_DISCONNECTED: tWapiFallbackToast = {
  sessionKey: WAPI_FALLBACK_TOAST_KEY,
  type: eAlertType.INFO,
  messageKey: 'wapiFallback.disconnected',
  offersReconnect: false,
};
const WAPI_FALLBACK_BY_ACTIVATION: Record<tWapiActivation, tWapiFallbackToast> =
  {
    pending: {
      sessionKey: `${WAPI_FALLBACK_TOAST_KEY}:activation-pending`,
      type: eAlertType.INFO,
      messageKey: 'wapiFallback.activationPending',
      offersReconnect: false,
    },
    failed: {
      sessionKey: `${WAPI_FALLBACK_TOAST_KEY}:activation-failed`,
      type: eAlertType.WARNING,
      messageKey: 'wapiFallback.activationFailed',
      offersReconnect: true,
    },
  };
// Long enough to read the sentence and reach the button (the SmartLeadbot
// config's precedent for a toast that asks for something).
const WAPI_FALLBACK_ACTION_DURATION_MS = 8000;

// Module-scoped because warm-cache membership must persist across useChat
// mount cycles (StrictMode double-invoke, route re-entry, provider remount)
// to correctly classify a 2nd-visit chat as 'warm'. A useRef would reset per
// component instance and mis-classify cache-hits as cold.
const warmSwitchCache = new Set<string>();

export function useChat(): tChatContext {
  const { addAlert } = useToastContext();
  const { t } = useTranslation('chat/chat');
  const { currentUserId } = useUserContext();
  const { currentWorkspace, getDisplayIntegration } = useWorkspaceContext();
  const { whatsappBusinessIntegration } = useIntegrationsContext();
  const { canUserAccessWhatsAppApi, userIsSeller } = useACLContext();
  const { trackEvent } = useAnalytics();
  const [funnelInfo, setFunnelInfo] = useLocalStorageState<tFunnelInfo[]>(
    'funnelData',
    {
      defaultValue: [],
      listenStorageChange: true,
    },
  );
  const [groupParticipants, setGroupParticipants] = useLocalStorageState<
    tParticipant[] | undefined
  >('groupParticipants', {
    defaultValue: [],
    listenStorageChange: true,
  });

  const defaultChannel = canUserAccessWhatsAppApi()
    ? eChannelType.WHATSAPP_API
    : eChannelType.WHATSAPP_WEB;

  const { decryptAndParseAllMessages } = useChatActions();
  const {
    selectedLeadForChatModal,
    setSelectedLeadForChatModal,
    tagOpsInFlight,
    setWhatsAppBusinessConnectionModalIsOpen,
  } = useModalsContext();

  // INT-941 / U1: live, non-cacheable WhatsApp Web connection status for the
  // open chat. Keyed on the REQUESTED integration (the field the channel
  // dropdown mutates) so it re-fires on a Web -> Business -> Web toggle even
  // when the resolved `selectedChat.integration` stays WHATSAPP_WEB via the
  // BUG-336 fallback below.
  //
  // BUG-896: the raw field alone is not that key. On a freshly-created lead it
  // is nullish while the send below already resolves to WhatsApp Web through
  // `|| defaultChannel`, so the probe went inert and the pre-send guard fell
  // through to a stale cache. `resolveProbeIntegration` closes that gap by
  // keying on the channel the send would actually use, without collapsing the
  // Web -> Business -> Web reactivity the raw field buys us.
  const { phase: connectionPhase, refetch: refetchConnection } = useWhatsAppWebConnection({
    workspaceId: currentWorkspace?.id,
    requestedIntegration: resolveProbeIntegration({
      hasOpenChat: selectedLeadForChatModal != null,
      requestedIntegration: selectedLeadForChatModal?.chat?.integration,
      defaultChannel,
      isWapiConnected:
        whatsappBusinessIntegration.state === eIntegrationStatus.CONNECTED,
    }),
  });

  const whatsAppWebConnection = useMemo(
    () => ({ phase: connectionPhase, refetch: refetchConnection }),
    [connectionPhase, refetchConnection],
  );
  const chatInputContainerRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const lastFetchedQuickRepliesWorkspaceRef = useRef<string | null>(null);
  const lastSwitchEmittedKeyRef = useRef<string>('');
  const [selectedChat, setSelectedChat] = useState<tCurrentChat | null>(null);
  const legacyChatId = selectedLeadForChatModal?.id ? selectedChat?.id : undefined;
  useLayoutEffect(() => retainLegacyChat(legacyChatId), [legacyChatId]);
  const [selectedChatLeadId, setSelectedChatLeadId] = useState<string>('');
  const [funnelsWithStages, setFunnelsWithStages] = useState<
    tFunnelsWithStages[] | null
  >(null);
  const [messages, setMessages] = useState<tChatEvents>();
  const [loading, setLoading] = useState<boolean>(true);
  const [blockTextArea, setBlockTextArea] = useState<boolean>(false);
  // RET-2232 D-16: único escritor = el efecto del sello del wallet en
  // `TextAreaContainer`. `SendAction` lee este campo en vez de
  // `blockTextArea` para decidir si el clic manda algo — ese otro campo
  // tiene seis escritores más que lo limpian sin saber nada del saldo.
  const [isComposerWalletBlocked, setComposerWalletBlocked] =
    useState<boolean>(false);
  // RET-2232 D-28: `useRef`, no `useState` — el único lector/escritor es
  // `useComposerServiceSeal`, y escribirlo no debe re-renderizar el árbol
  // del chat. Vive aquí por la misma razón que `isComposerWalletBlocked`
  // arriba: sobrevive al remontaje de `TextAreaContainer` que
  // `ChatEnum.WABTEMPLATES_VARIABLES` dispara sin cambiar de conversación.
  const composerSealQuoteRef = useRef<tComposerSealQuoteScope>(null);
  const [selectedTab, setSelectedTab] = useState<ChatInfoEnum>(
    ChatInfoEnum.ATTRIBUTES,
  );
  const [replyMessage, setReplyMessage] = useState<tChatEvent | null>(null);
  const [resendIds, setResendIds] = useState<string[]>([]);
  const [selectedChatsIds, setSelectedChatsIds] = useState<string[]>([]);
  const [cursor, setCursor] = useState<string>('');
  const [rows, setRows] = useState(0);
  const [updateChatEventsData, setUpdateChatEventsData] = useState<number>(0);
  const [selectedMessageContainer, setSelectedMessageContainer] =
    useState<ChatMessageContainerEnum>(ChatMessageContainerEnum.CHAT);
  const [selectedTextAreaContainer, setSelectedTextAreaContainer] =
    useState<ChatTextAreaContainerEnum>(ChatTextAreaContainerEnum.TEXTAREA);
  const [showAllowTypesBanner, setShowAllowTypesBanner] =
    useState<boolean>(false);
  const [followUps, setFollowUps] = useState<tChatEvent[]>([]);
  const [notes, setNotes] = useState<tChatEvent[]>([]);
  const [leadsalesAI, setLeadsalesAI] = useState<tChatEvent[]>([]);
  const [favoriteMessages, setFavoriteMessages] = useState<tChatEvent[]>([]);
  const [quickReplies, setQuickReplies] = useState<tQuickReply[]>([]);
  const [selectedNote, setSelectedNote] = useState<tSelectedNote | null>(null);
  const [selectedFollowUp, setSelectedFollowUp] =
    useState<tSelectedFollowUp | null>(null);
  const [quickActionState, setQuickActionState] = useState<tQuickActionState>({
    id: '',
    isPublic: false,
    title: '',
    text: '',
    attachments: [],
  });
  const [isResending, setIsResending] = useState<boolean>(false);
  const [droppedAttachments, setDroppedAttachments] = useState<
    tChatAttachment[]
  >([]);
  const droppedAttachmentUrls = useMemo(() => droppedAttachments.map((attachment) => attachment.src), [droppedAttachments]);
  useBlobUrlHolder(droppedAttachmentUrls);
  const [chatEvents, setChatEvents] = useState<tChatEvent[]>([]);
  const [latestPage, setLatestPage] = useState<tLatestChatPage | null>(null);
  // Read after the await in returnToLatest: a page that lands once the user has
  // moved to another chat belongs to nobody on screen and must be dropped.
  const openChatIdRef = useRef<string | null>(null);
  openChatIdRef.current = selectedChat?.id ?? null;
  const [templatePickerRequest, setTemplatePickerRequest] =
    useState<tTemplatePickerRequest | null>(null);
  const [takeConversationRequest, setTakeConversationRequest] =
    useState<tTakeConversationRequest | null>(null);
  const [newFunnelID, setNewFunnelID] = useState<string>('');
  const [autoRespondEnabled, setAutoRespondEnabled] = useState<boolean>(false);
  const { refetch: refetchLeadAgentPaused, paused, reason: pauseReason } =
    useLeadAgentPaused(currentWorkspace?.id);
  const leadAgentPaused = useMemo(
    () => ({ paused, reason: pauseReason }),
    [paused, pauseReason],
  );
  const tagInputRef = useRef<HTMLInputElement>(null);
  // BUG-534: the throttle Daniel introduced in PR #2425 as a per-hook useRef
  // is now session-scoped in `staleFunnelsChecked.ts`, so the
  // `FunnelCacheInvalidationProvider`'s MERGE handler can re-open it on
  // every `funnel_event.updated`. See plan U3 + round-2 finding A1.

  // Summary states
  const [conversationSummary, setConversationSummary] =
    useState<tConversationSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<tSummaryError | null>(null);
  const [summaryEligibility, setSummaryEligibility] =
    useState<tSummaryEligibilityResponse | null>(null);

  const selectMessageToReply = useCallback(
    (messageSelected: boolean, id: string) => {
      setResendIds((prev) =>
        messageSelected
          ? prev.filter((resendId) => resendId !== id)
          : [...prev, id],
      );
    },
    [],
  );

  const getFunnelsWithStages = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setLoading(true);

    try {
      const response = await PipeService.getWorkspaceFunnelsWithStages(
        currentWorkspace?.id,
      );

      setFunnelsWithStages(response.data.pipes);
    } catch (err) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer los embudos para el modal',
      });
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, addAlert]);

  const fetchQuickReplies = useCallback(
    async (signal?: AbortSignal) => {
      // Snapshot the workspace id at call time so the response can be
      // matched against the ref after the `await`. Prevents a stale
      // response from a previous workspace from overwriting the current
      // workspace's list (cross-workspace data leak).
      const callWorkspaceId = currentWorkspace?.id;
      if (!callWorkspaceId) return;
      try {
        const response = await ChatService.getQuickReplies(callWorkspaceId, {
          signal,
        });
        if (signal?.aborted) return;
        // Stale-response guard: if the effect has since latched onto a
        // different workspace, drop this response on the floor.
        if (lastFetchedQuickRepliesWorkspaceRef.current !== callWorkspaceId) {
          return;
        }
        setQuickReplies(response);
      } catch (error) {
        // Aborted by effect cleanup or request was canceled — silent.
        if (signal?.aborted || isCancel(error)) return;
        // Release the ref so the next effect run (workspace revisit) can
        // retry instead of being permanently locked on a failed fetch.
        if (lastFetchedQuickRepliesWorkspaceRef.current === callWorkspaceId) {
          lastFetchedQuickRepliesWorkspaceRef.current = null;
        }
        addAlert({
          type: eAlertType.ERROR,
          message: `Error fetching quick replies: ${error}`,
        });
      }
    },
    [currentWorkspace?.id, addAlert],
  );

  async function resetFunnelInfo(
    funnelId: string,
    signal?: AbortSignal,
  ): Promise<tLeadInfo | null> {
    if (!currentWorkspace || isEmpty(currentWorkspace?.id)) return null;

    let currentFunnelInfo =
      funnelInfo?.find((funnel) => funnel.uuid === funnelId) || null;

    const leadInfo: tLeadInfo = {
      // Stamp the funnel these stages belong to, so a caller that writes a
      // stage names its real owner instead of guessing from the view (BUG-288).
      funnelId,
      stages: [],
      statuses: [],
      users: [],
    };

    if (currentFunnelInfo) {
      leadInfo.stages = currentFunnelInfo.stages;
      leadInfo.statuses = currentFunnelInfo.statuses;
      leadInfo.users = currentFunnelInfo.users;

      // BUG-534 family. Two conditions fire a background `getWorkspacesInfo`
      // refetch from the cache-hit branch:
      //
      //   1. Daniel's PR #2425 — `can_view_handle: false` in cache might be
      //      stale; the owner could have flipped permission since.
      //
      //   2. This plan (U3) — `users_stale: true` set by the
      //      FunnelCacheInvalidationProvider's MERGE handler, OR a legacy
      //      pre-deploy snapshot (no `cache_version` field). The
      //      `users_stale` flag means `funnel_event.updated` arrived while
      //      the user wasn't looking; the wire payload never carries
      //      `users[]`, so we lazy-refresh via this fetch.
      //
      // Throttled by the session-scoped `staleFunnelsChecked` set (was
      // Daniel's per-hook useRef; promoted to module scope so the provider
      // can re-open it on every fresh merge). One fetch per funnel per
      // session, re-opened by `FunnelCacheInvalidationProvider`'s
      // `clearFunnelChecked` on each merge.
      const isLegacySnapshot = currentFunnelInfo.cache_version === undefined;
      const needsUsersRefresh =
        isLegacySnapshot || currentFunnelInfo.users_stale === true;
      const needsHandleRefresh = !currentFunnelInfo.can_view_handle;
      const versionAtDispatch = currentFunnelInfo.cache_version ?? 0;

      if (
        funnelId !== NIL &&
        (needsHandleRefresh || needsUsersRefresh) &&
        !wasFunnelChecked(funnelId)
      ) {
        markFunnelChecked(funnelId);
        // Plumb the caller's AbortSignal when present so a chat-switch /
        // workspace-switch mid-fetch can cancel the background work.
        // Match the cache-miss path's call shape conditionally (omit the
        // third arg entirely when no signal) so callers that don't pass a
        // signal still produce a 2-arg invocation — the existing
        // BG-vs-cache-miss test discriminator relies on argument arity.
        const bgFetch = signal
          ? ChatService.getWorkspacesInfo(currentWorkspace.id, funnelId, {
              signal,
            })
          : ChatService.getWorkspacesInfo(currentWorkspace.id, funnelId);
        bgFetch
          .then((freshInfo) => {
            if (!freshInfo) return;
            if (signal?.aborted) return;
            if (wasFunnelRecentlyEvicted(funnelId)) return;
            setFunnelInfo((prev) =>
              (prev || []).map((f) => {
                if (f.uuid !== funnelId) return f;
                // BUG-534 race-safe DISCARD (replaces the partial
                // race-safe-clear from PR #2429 round-1). If a concurrent
                // MERGE (or U4 authoritative `applyWorkspaceUsersUpdateToFunnelData`
                // write) bumped `cache_version` between this fetch's
                // dispatch and commit, the entry in cache is now
                // authoritative for ALL fields: scalars, arrays, and
                // `users[]`. Our `freshInfo` is pre-merge data; overwriting
                // even a single field would clobber the merger's work.
                // Preserve the entry as-is.
                const currentVersion = f.cache_version ?? 0;
                if (currentVersion !== versionAtDispatch) return f;
                return {
                  ...f,
                  // Scalars — boundary-coerced by the merger / fresh from
                  // backend, both authoritative when no concurrent merge.
                  name: freshInfo.name,
                  description: freshInfo.description,
                  emoji: freshInfo.emoji,
                  is_default: freshInfo.is_default,
                  is_public: freshInfo.is_public,
                  can_view_handle: freshInfo.can_view_handle,
                  from_industry_template: freshInfo.from_industry_template,
                  stages: freshInfo.stages,
                  statuses: freshInfo.statuses,
                  users: freshInfo.users,
                  users_stale: false,
                  cache_version: currentVersion + 1,
                };
              }),
            );
          })
          .catch(() => {
            // Background-only; non-fatal. Release the session-scoped
            // throttle so the next caller can retry — otherwise a single
            // transient 401/timeout/network blip (or a deliberate abort
            // mid-flight) locks the funnel's lazy refetch until the next
            // provider-driven `clearFunnelChecked` or a full page reload
            // (PR #2429 review finding #1).
            clearFunnelChecked(funnelId);
          });
      }
    } else {
      if (!funnelId || funnelId === NIL) return null;

      currentFunnelInfo = await ChatService.getWorkspacesInfo(
        currentWorkspace?.id,
        funnelId,
        signal ? { signal } : undefined,
      );

      if (currentFunnelInfo) {
        leadInfo.stages = currentFunnelInfo.stages;
        leadInfo.statuses = currentFunnelInfo.statuses;
        leadInfo.users = currentFunnelInfo.users;
        // BUG-534 (was INT-945): if a `funnel_event.updated` arrived while
        // the fetch above was in flight, the provider already wrote a
        // fresh merged entry. The response we just received may be a
        // pre-merge snapshot — caching it would overwrite the merged entry
        // with stale data. Return the fresh data to the immediate caller
        // either way; only the cache write is suppressed.
        if (!wasFunnelRecentlyEvicted(funnelId)) {
          // Stamp `cache_version: 1` so the next read doesn't treat this
          // entry as a legacy pre-deploy snapshot and force a needless
          // legacy-migration refetch.
          const narrowedFunnelInfo: tFunnelInfo = {
            ...currentFunnelInfo,
            cache_version: 1,
          };
          setFunnelInfo((prev) => [...(prev || []), narrowedFunnelInfo]);
        }
      }
    }

    return leadInfo;
  }

  async function getOrCreateChatData(): Promise<{
    chatId: string;
    channels: eChannelType[];
    state: ChatState;
    integration: eChannelType;
    freeEntryPointData?: tFreeEntryPointData;
    sentTemplateCounter?: tSentTemplateCounter;
    undeliveredMessage?: tUndeliveredMessage;
    isBlocked?: boolean;
    // Set when the lead genuinely has no address on this channel, so the caller
    // can tell "nothing to open" apart from "the backend failed to open it".
    noHandle?: boolean;
  }> {
    if (
      !selectedLeadForChatModal ||
      !currentWorkspace ||
      isEmpty(currentWorkspace?.id)
    )
      return {
        chatId: '',
        channels: [],
        state: ChatState.ACTIVE,
        integration: eChannelType.WHATSAPP_WEB,
      };

    const requestedIntegration = getDisplayIntegration(
      selectedLeadForChatModal?.chat?.integration || defaultChannel,
      defaultChannel,
    );

    // BUG-336: After a WAPI downgrade, leads stay tagged with
    // chat.integration === WHATSAPP_API even though the workspace's WAPI
    // account is gone. Loading those chats fails downstream because every
    // WAPI-scoped fetch (templates, account info, presence) 404s. Fall back
    // to WHATSAPP_WEB so the existing create-WEB-chat path below can run
    // and the user can keep working in WhatsApp Web.
    const isWapiConnected =
      whatsappBusinessIntegration.state === eIntegrationStatus.CONNECTED;
    const wapiFallbackTriggered =
      requestedIntegration === eChannelType.WHATSAPP_API && !isWapiConnected;
    const integration = wapiFallbackTriggered
      ? eChannelType.WHATSAPP_WEB
      : requestedIntegration;

    const activation = resolveWapiActivation(whatsappBusinessIntegration.state);
    const fallbackToast = activation
      ? WAPI_FALLBACK_BY_ACTIVATION[activation]
      : WAPI_FALLBACK_DISCONNECTED;

    if (
      wapiFallbackTriggered &&
      !sessionStorage.getItem(fallbackToast.sessionKey)
    ) {
      sessionStorage.setItem(fallbackToast.sessionKey, '1');
      // Sellers cannot resume the connection from the Dashboard card or the
      // WhatsApp API page either; they keep the explanation, not the button.
      const canReconnect = fallbackToast.offersReconnect && !userIsSeller;
      addAlert({
        type: fallbackToast.type,
        message: t(fallbackToast.messageKey),
        ...(canReconnect && {
          duration: WAPI_FALLBACK_ACTION_DURATION_MS,
          action: {
            label: t('wapiFallback.reconnect'),
            onClick: () => setWhatsAppBusinessConnectionModalIsOpen(true),
          },
        }),
      });
    }

    // BUG-792: a deep link (?leadId=…) from Agenda/Directorio hydrates the lead from
    // page 1 of the inbox search; when the lead isn't on that page (25 rows out of ~9k
    // for a real workspace) Messenger.hook falls back to a minimal `{ id, chat }` object
    // with no handle. The handle does arrive ~200ms later from the "Get Lead info"
    // effect below, but this effect doesn't re-run on it — so the chat stayed shut on a
    // conversation that exists and is healthy. Ask leads-service for the handle here
    // rather than giving up: one code path covers every entry point (Agenda, Directorio,
    // pasted URL, notification), and the Bandeja path never reaches it.
    let handle = selectedLeadForChatModal?.handle ?? '';

    if (!handle) {
      try {
        const leadResponse = await ChatService.getLead({
          workspaceId: currentWorkspace.id,
          leadId: selectedLeadForChatModal.id,
        });
        handle = leadResponse.data.handle ?? '';
      } catch (error) {
        console.warn(
          '[Chat] getOrCreateChatData: handle hydration failed',
          error,
        );
      }
    }

    // Only now, with leads-service having answered, is "no handle" a real answer: the
    // lead has no address on this channel and the chat lookup answers a bare 500 when
    // asked with `handle=` empty. Refuse here, in words, instead of forwarding a request
    // that cannot succeed — same empty contract as the no-lead early return above, so
    // every consumer already handles it.
    if (!handle) {
      addAlert({
        type: eAlertType.INFO,
        message: t('chatUnavailableNoHandle'),
      });
      return {
        chatId: '',
        channels: [],
        state: ChatState.ACTIVE,
        integration,
        noHandle: true,
      };
    }

    const data = {
      workspaceId: currentWorkspace?.id,
      leadId: selectedLeadForChatModal.id,
      integration,
      handle,
    };

    let chatInfo = await ChatService.getChatID(data);
    let chatId = chatInfo.data.id;

    if (!chatId && integration === eChannelType.WHATSAPP_WEB) {
      const requestForChat: tRequestChat = {
        workspace_id: currentWorkspace?.id,
        channel_type: eChannelType.WHATSAPP_WEB,
        // The hydrated local `handle`, not the modal's: on a deep link the modal
        // copy is still empty, and this branch became reachable with it once the
        // `if (!handle)` early return stopped firing first (BUG-792). Posting
        // `handle: ''` here would create a chat with no address.
        handle,
        lead_id: selectedLeadForChatModal.id,
      };
      const responseCreateIntegrationLead =
        await PipeService.createIntegrationLead(
          selectedLeadForChatModal.id,
          requestForChat,
        );
      chatId = responseCreateIntegrationLead.id;

      chatInfo = await ChatService.getChatID(data);
    }

    return {
      chatId,
      channels: chatInfo?.data?.channels || [],
      state: (chatInfo?.data?.state as ChatState) ?? ChatState.ACTIVE,
      integration: chatInfo?.data?.integration,
      freeEntryPointData: chatInfo.data.freeEntryPointData,
      sentTemplateCounter: chatInfo.data.sentTemplateCounters,
      undeliveredMessage: chatInfo.data.undeliveredMessages,
      isBlocked: chatInfo?.data?.isBlocked ?? false,
    };
  }

  async function fetchLeadInfo(signal?: AbortSignal) {
    const leadId = selectedLeadForChatModal?.id;
    const workspaceId = currentWorkspace?.id;

    let funnelId =
      (selectedLeadForChatModal?.funnel?.uuid !== NIL &&
        selectedLeadForChatModal?.funnel?.uuid) ||
      localStorage.getItem('currentFunnelID')?.replace(/"/g, '') ||
      '';

    if (!funnelId && leadId && workspaceId) {
      try {
        const leadResponse = await ChatService.getLead(
          { workspaceId, leadId },
          { signal },
        );
        if (!signal?.aborted) {
          funnelId = leadResponse.data.pipeId ?? '';
        }
      } catch (error) {
        console.warn('[Chat] fetchLeadInfo: getLead fallback failed', error);
      }
    }

    return await resetFunnelInfo(funnelId, signal);
  }

  /**
   * set Selected Tab
   */
  useEffect(() => {
    setSelectedTab(ChatInfoEnum.ATTRIBUTES);
  }, []);

  /**
   * Reset Funnel Info
   */
  useEffect(() => {
    if (newFunnelID) {
      (async () => {
        try {
          if (newFunnelID === NIL) {
            setNewFunnelID('');
            setSelectedChat((prev) => {
              if (!prev) return null;
              return {
                ...prev,
                lead: {
                  ...prev.lead,
                  // Inbox has no funnel: clear the owner with the list it owns.
                  funnelId: '',
                  stages: [],
                  statuses: [],
                },
              };
            });
            return;
          }

          const leadInfo = await resetFunnelInfo(newFunnelID);

          if (!leadInfo) {
            throw new Error(t('errors.leadInfoFailed'));
          }
          setSelectedChat((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              lead: leadInfo,
            };
          });
        } catch (error) {
          addAlert({
            type: eAlertType.ERROR,
            message: (error as Error).message,
          });
        } finally {
          setNewFunnelID('');
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18: Funnel hydration remains keyed to the requested funnel id.
  }, [newFunnelID]);

  /**
   * Get ChatID
   * Get Chat
   * Get Templates
   * Get More Files
   * Get Lead Info
   */
  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    if (
      !currentWorkspace ||
      isEmpty(currentWorkspace?.id) ||
      !selectedLeadForChatModal?.id ||
      newFunnelID
    ) {
      return;
    }

    const t0 = performance.now();

    // Capture context-object IDs at effect start. Context objects are read
    // after several awaits below; capturing up-front prevents a mid-load
    // provider mutation from silently shifting the workspace/lead id on us
    // (pattern per docs/solutions/logic-errors/websocket-race-overwrites-
    // guard-state-during-await.md; same shape as the PR 2 quick-replies
    // fetch at ~line 194).
    const capturedWorkspaceId = currentWorkspace.id;
    const capturedLeadId = selectedLeadForChatModal.id;

    const loadChat = async () => {
      try {
        setLoading(true);
        setMessages({ events: [], cursor: '' });
        setSelectedChat(null);
        setSelectedChatLeadId('');
        setBlockTextArea(true);

        const {
          chatId,
          channels,
          state,
          integration,
          freeEntryPointData,
          sentTemplateCounter,
          undeliveredMessage,
          isBlocked,
          noHandle,
        } = await getOrCreateChatData();
        // A lead with no address on this channel already surfaced
        // `chatUnavailableNoHandle` above; stacking chatCreateFailed on top of it
        // reported a backend failure that never happened (BUG-792).
        if (!chatId) {
          if (noHandle) return;
          throw new Error(t('errors.chatCreateFailed'));
        }

        if (!isCurrent) return;

        const releaseLegacyChat = retainLegacyChat(chatId);
        controller.signal.addEventListener('abort', () => releaseLegacyChat?.(), { once: true });

        const chatResponse = await ChatService.getChat({
          workspaceId: capturedWorkspaceId,
          leadId: capturedLeadId,
          chatId,
        });

        if (!isCurrent) return;

        setMessages({
          events: chatResponse.data.chatEvents.events,
          cursor: chatResponse.data.chatEvents.cursor,
        });
        setCursor(chatResponse.data.chatEvents.cursor);
        const t1 = performance.now();

        if (!isCurrent) return;

        // PR 3 — Phase 3 + Phase 4 fan out concurrently.
        //
        // Critical batch: participants / templates / leadInfo. Any failure
        // here throws and aborts the whole load (today's behavior).
        //
        // Tolerated batch: 4 side-panel endpoints that used to live in
        // orphan useEffects firing AFTER setSelectedChat. Moving them here
        // (a) parallelizes them with the critical batch instead of waiting
        // for setSelectedChat to commit, (b) collapses the 3–5× re-fire
        // pattern observed in Birdie (StrictMode + [selectedChat] deps),
        // (c) structurally prevents a single side-panel failure from
        // blocking chat hydration via Promise.allSettled.
        //
        // The outer Promise.all kicks both batches off in the same tick.
        // Every fetch — critical and tolerated — is passed the same
        // `controller.signal` so rapid chat switches abort in-flight
        // network work instead of letting zombies complete against the
        // stale chat.
        const criticalPromise = Promise.all([
          ChatService.getParticipants(chatId, { signal: controller.signal }),
          ChatService.getTemplates(chatId, { signal: controller.signal }),
          fetchLeadInfo(controller.signal),
        ]);

        const tolerantPromise = Promise.allSettled([
          ChatService.getFollowUps({
            chatId,
            config: { signal: controller.signal },
          }),
          ChatService.getNotes(chatId, { signal: controller.signal }),
          ChatService.getLeadsalesAI(chatId, { signal: controller.signal }),
          ChatService.getFavoriteMessages(chatId, {
            signal: controller.signal,
          }),
        ]);

        const [criticalResults, tolerantResults] = await Promise.all([
          criticalPromise,
          tolerantPromise,
        ]);
        const [participantsResponse, templatesResponse, leadInfo] =
          criticalResults;
        const [
          followUpsResult,
          notesResult,
          leadsalesAIResult,
          favoriteMessagesResult,
        ] = tolerantResults;

        // Drive files are no longer preloaded - they will only load when user clicks attachment button
        const driveFilesResponse = {
          data: { cursor: '', files: [] },
        };

        if (!isCurrent || controller.signal.aborted) return;

        if (!leadInfo) {
          throw new Error(t('errors.leadInfoLoadFailed'));
        }

        // Reconcile chatEvents with any socket-arrived events that landed
        // during the Phase 3/4 wait window — using a functional setter so
        // the merge sees the latest state, not a stale snapshot. Without
        // this, messages emitted via the socket after Phase 2 but before
        // we commit would be clobbered by the delayed setSelectedChat
        // path (`julik` race bug #1). The outer guard mirrors every other
        // tolerated setter below so a mid-load abort doesn't enqueue the
        // merge reducer against a future chat's state.
        if (!controller.signal.aborted) {
          setMessages((prev) =>
            mergeChatEventsByEventId(prev, chatResponse.data.chatEvents),
          );
        }

        setGroupParticipants(participantsResponse);

        const nextSelectedChat: tCurrentChat = {
          id: chatId,
          chatEvents: chatResponse.data.chatEvents,
          lead: leadInfo,
          leadId: capturedLeadId,
          templates: templatesResponse || [],
          participants: participantsResponse || [],
          channels: channels || [],
          state: state as ChatState,
          integration: integration,
          driveFiles: {
            cursor: driveFilesResponse.data.cursor,
            files: driveFilesResponse.data.files,
          },
          freeEntryPointData: freeEntryPointData,
          sentTemplateCounter: sentTemplateCounter,
          undeliveredMessage: undeliveredMessage,
          isBlocked: isBlocked ?? false,
        };

        // Tolerated-panel setters: each degrades to empty on rejection.
        // The per-setter `signal.aborted` guard stops setState cascades
        // if cleanup fired between `await Promise.all(...)` resolving
        // and this synchronous block.
        const sidePanelState = unwrapTolerantSidePanelResults([
          followUpsResult,
          notesResult,
          leadsalesAIResult,
          favoriteMessagesResult,
        ]);
        if (!controller.signal.aborted) {
          setFollowUps(sidePanelState.followUps);
          setNotes(sidePanelState.notes);
          setLeadsalesAI(sidePanelState.leadsalesAI);
        }

        // Favorites decryption is async — re-check signal AND isCurrent
        // after the await because cleanup may have fired in the meantime.
        // Wrap in a try/catch so a decryption failure (bad ciphertext,
        // key rotation) clears the panel instead of cascading into the
        // outer catch and aborting the whole chat load — which would
        // otherwise leave the panel pinned to the *previous* chat's
        // favorites while every other pane updated.
        if (
          sidePanelState.rawFavoriteMessages !== null &&
          !controller.signal.aborted
        ) {
          try {
            const decryptedFavorites = await decryptAndParseAllMessages(
              sidePanelState.rawFavoriteMessages,
              chatId,
            );
            if (!controller.signal.aborted && isCurrent) {
              setFavoriteMessages(decryptedFavorites);
            }
          } catch {
            if (!controller.signal.aborted && isCurrent) {
              setFavoriteMessages([]);
            }
          }
        } else if (
          sidePanelState.rawFavoriteMessages === null &&
          !controller.signal.aborted
        ) {
          // Rejected fetch — clear favorites panel (same as today's
          // catch-and-swallow behavior in the deleted orphan useEffect).
          setFavoriteMessages([]);
        }

        setSelectedChat(nextSelectedChat);
        setSelectedChatLeadId(capturedLeadId);

        const emitKey = `${chatId}:${capturedWorkspaceId}`;
        if (lastSwitchEmittedKeyRef.current !== emitKey) {
          lastSwitchEmittedKeyRef.current = emitKey;
          const cacheClass = classifyChatSwitchCacheClass(
            chatId,
            warmSwitchCache,
          );
          const eventsCount = chatResponse.data.chatEvents.events.length;
          requestAnimationFrame(() => {
            if (!isCurrent) return;
            // Sampled 1:10 — this was the single largest event in the project
            // (12.4M/mo, 39% of volume). Multiply counts by 1/sample_rate when
            // reading volumes; latency percentiles are unaffected by sampling.
            if (Math.random() >= CHAT_SWITCH_DURATION_SAMPLE_RATE) return;
            const event: tChatSwitchDurationEvent = {
              chatId,
              integration,
              toDataReadyMs: Math.round(t1 - t0),
              toReadyMs: Math.round(performance.now() - t0),
              cacheClass,
              eventsCount,
              connection: getNetworkEffectiveType(),
            };
            trackEvent('front.chat.switch.duration', {
              ...event,
              sample_rate: CHAT_SWITCH_DURATION_SAMPLE_RATE,
            });
          });
        }
      } catch (error) {
        // Cleanup fired (chat switch) or request was canceled — silent.
        if (!isCurrent || isCancel(error)) return;
        addAlert({
          type: eAlertType.ERROR,
          message:
            (error as Error).message ||
            'Ocurrió un error al obtener la información del chat',
        });
      } finally {
        if (isCurrent) {
          setLoading(false);
          setBlockTextArea(false);
        }
      }
    };

    loadChat();

    return () => {
      isCurrent = false;
      controller.abort(); // cancel all requests if the lead/chat changes
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18: Keep the existing chat-load cancellation keys; handle hydration must not restart the load (BUG-792).
  }, [
    currentWorkspace?.id,
    selectedLeadForChatModal?.id,
    selectedLeadForChatModal?.chat?.integration,
    // Deliberately NOT depending on `selectedLeadForChatModal?.handle` (BUG-792):
    // getOrCreateChatData hydrates the handle itself, so a late handle write from the
    // "Get Lead info" effect below would only abort the load already in flight and
    // redo it. Hydration lives in one place, not in this dependency list.
    addAlert,
    updateChatEventsData,
  ]);

  // BUG-1126: every change of the open lead's `active` is counted, so the
  // lead read below can tell that one happened while it was in flight even
  // when the value flipped back (true -> false -> true).
  const activeChangeCountRef = useRef(0);
  const lastLeadActiveRef = useRef<{ id?: string; active?: boolean }>({});
  useEffect(() => {
    const previous = lastLeadActiveRef.current;
    lastLeadActiveRef.current = {
      id: selectedLeadForChatModal?.id,
      active: selectedLeadForChatModal?.active,
    };
    if (
      previous.id === selectedLeadForChatModal?.id &&
      previous.active !== selectedLeadForChatModal?.active
    ) {
      activeChangeCountRef.current += 1;
    }
  }, [selectedLeadForChatModal?.id, selectedLeadForChatModal?.active]);

  /**
   * Get Lead info
   */
  useEffect(() => {
    if (
      !currentWorkspace ||
      isEmpty(currentWorkspace?.id) ||
      !selectedLeadForChatModal?.id
    ) {
      return;
    }

    let isCurrent = true;
    // BUG-1126: archiving right after opening the chat lands before this read,
    // and the read predates it. If `active` changed at all while it was in
    // flight, the current value is newer than the answer and is kept.
    const activeChangesAtStart = activeChangeCountRef.current;

    (async () => {
      try {
        const responseLead = await ChatService.getLead({
          workspaceId: currentWorkspace?.id,
          leadId: selectedLeadForChatModal?.id,
        });

        if (!isCurrent) return;

        const leadUsers = responseLead.data.users;
        setSelectedLeadForChatModal((selectedLeadForChatModal) => {
          if (!selectedLeadForChatModal) return null;
          return {
            ...selectedLeadForChatModal,
            active:
              activeChangeCountRef.current !== activeChangesAtStart
                ? selectedLeadForChatModal.active
                : responseLead.data.active,
            assignedUser: {
              authID: responseLead.data.assigneeAuthId,
              name:
                leadUsers?.find(
                  (user) => user.auth_id === responseLead?.data?.assigneeAuthId,
                )?.name || '',
            },
            company: responseLead.data.company,
            currentStage: responseLead.data.currentStage,
            email: responseLead.data.email,
            handle: responseLead.data.handle,
            value: responseLead.data.value,
            id: responseLead.data.id,
            name: responseLead.data.name,
            profilePicture: responseLead.data.profilePicture,
            revenue: responseLead.data.revenue,
            status: {
              id:
                responseLead.data.status ??
                selectedLeadForChatModal?.status?.id ??
                '',
              name:
                responseLead.data.status ??
                selectedLeadForChatModal?.status?.name ??
                '',
            },
            stage: {
              id:
                responseLead.data.currentStage ??
                selectedLeadForChatModal?.stage?.id ??
                '',
              name:
                responseLead.data.stages?.find(
                  (stage) => stage.id === responseLead.data.currentStage,
                )?.name ??
                selectedLeadForChatModal?.stage?.name ??
                '',
            },
            funnel: {
              uuid:
                responseLead.data.pipeId ??
                selectedLeadForChatModal?.funnel?.uuid ??
                '',
              name: selectedLeadForChatModal?.funnel?.name ?? '',
              isDefault: selectedLeadForChatModal?.funnel?.isDefault ?? false,
            },
            tags:
              tagOpsInFlight.current > 0
                ? selectedLeadForChatModal.tags
                : normalizeLeadTags(responseLead.data.tags),
            chat: {
              chatID: selectedLeadForChatModal?.chat?.chatID ?? '',
              // API response takes precedence; corrects stale integration on first open.
              integration:
                responseLead.data.chat?.integration ??
                selectedLeadForChatModal?.chat?.integration ??
                eChannelType.WHATSAPP_WEB,
              lastMessageOn:
                selectedLeadForChatModal?.chat?.lastMessageOn ?? null,
              lastMessageText:
                selectedLeadForChatModal?.chat?.lastMessageText ?? null,
              lastMessageType:
                selectedLeadForChatModal?.chat?.lastMessageType ?? null,
              read: responseLead.data.read,
              unreadCount: responseLead.data.unreadMessages,
            },
          } as tAdvanceSearchLead;
        });
      } catch (error) {
        console.error('[Chat] getLead failed:', error);
      }
    })();

    return () => {
      isCurrent = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18: Lead hydration runs per workspace/lead id, not on its own modal writes.
  }, [currentWorkspace?.id, selectedLeadForChatModal?.id]);

  // Side-panel hydration effects (FollowUps / Notes / LeadsalesAI /
  // FavoriteMessages) used to live here as four independent
  // `useEffect`s that waited for `setSelectedChat` to commit before
  // firing. As of PR 3 / INT-815 they're folded into the main chat-load
  // `Promise.allSettled` batch above, which (a) parallelizes them with
  // the critical hydration calls instead of stacking them as a post-
  // commit waterfall, and (b) collapses the 3–5× re-fire-per-chat
  // pattern the side-panels exhibited under StrictMode + reference-
  // churn on `[selectedChat]` deps.

  /**
   * Fetch QuickReplies once per workspace.
   *
   * Gated on `lastFetchedQuickRepliesWorkspaceRef`: the effect fires on
   * workspace change, clears the local list on the commit that follows,
   * and starts a new fetch. Same-workspace chat switches do not re-enter
   * (dep is `[currentWorkspace?.id]`). Mutations refetch from the call
   * site (see CreateQuickAction / QuickActionRow / PreviewSaveQuickReply);
   * `fetchQuickReplies` itself rejects stale responses whose call-time
   * workspace id no longer matches the ref.
   */
  useEffect(() => {
    const wid = currentWorkspace?.id;
    if (!wid) return;

    if (lastFetchedQuickRepliesWorkspaceRef.current === wid) return;
    lastFetchedQuickRepliesWorkspaceRef.current = wid;

    setQuickReplies([]);

    const controller = new AbortController();
    void fetchQuickReplies(controller.signal);
    return () => controller.abort();
  }, [currentWorkspace?.id, fetchQuickReplies]);

  /**
   * Clear summary when chat changes
   */
  useEffect(() => {
    // Do nothing on mount, cleanup runs when component unmounts or before selectedChat.id changes
    return () => {
      if (selectedChat?.id) {
        clearSummary();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- U18: Summary cleanup remains keyed only to the selected chat id.
  }, [selectedChat?.id]);

  const { metaMessengerTemplateUpdate, catchUpEvent } = useSocketsContext();

  metaMessengerTemplateUpdate.useSubscription((template: tTemplates) => {
    if (
      !selectedChat ||
      template?.handle !== selectedLeadForChatModal?.handle
    ) {
      return;
    }
    setSelectedChat({
      ...selectedChat,
      state: template.new_chat_state,
    });
  });

  catchUpEvent.useSubscription(() => {
    setUpdateChatEventsData((prev) => prev + 1);
  });

  // Summary functions
  const createSummaryAIEvent = async (
    summary: tConversationSummary,
  ): Promise<void> => {
    try {
      if (!currentUserId || !selectedChat?.id) {
        return;
      }

      const summaryEventId = uuid();
      const summaryTitle = `📝 Resumen de Conversación - ${new Date().toLocaleDateString()}`;

      // Create API request for persistence
      const createRequest: tCreateSummaryAIRequest = {
        chatId: selectedChat.id,
        data: {
          id: summaryEventId,
          content: {
            text: summary.content,
            attachments: [], // Summaries don't have attachments
          },
          created_by: currentUserId,
          title: summaryTitle,
          leadsales_ai: {
            title: summaryTitle,
            type: 1, // Type 1 for summary
          },
        },
      };

      // Persist to backend
      await ChatService.createSummaryAIEvent(createRequest);

      // Create local event for immediate UI update
      const newSummaryEvent: tChatEvent = {
        agendaEvent: null,
        chatID: selectedChat.id,
        content: {
          attachments: [],
          linkPreview: null,
          quotedEventID: null,
          text: summary.content,
        },
        createdAt: new Date().toISOString(),
        createdBy: currentUserId,
        deletedAt: '',
        followUp: null,
        id: summaryEventId,
        message: null,
        note: {
          title: summaryTitle,
        },
        type: EventType.LEADSALES_AI,
        updatedAt: new Date().toISOString(),
      };

      // Add to local states for immediate UI update
      setLeadsalesAI([newSummaryEvent, ...leadsalesAI]);
      setChatEvents([...chatEvents, newSummaryEvent]);
    } catch (error) {
      console.error('Failed to create summary AI event:', error);
      // Don't throw error - summary generation was successful
    }
  };

  function formatMessagesForPrompt(messages: tChatEvent[]): string {
    const validMessages = messages
      .filter((msg) => msg.content?.text)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

    return validMessages
      .map((msg) => {
        const timestamp = new Date(msg.createdAt).toLocaleString('es-ES');
        const sender = msg.message?.fromMe ? 'Agente' : 'Cliente';
        return `[${timestamp}] ${sender}: ${msg.content?.text?.trim() || ''}`;
      })
      .join('\n');
  }

  function calculateParticipantCount(messages: tChatEvent[]): number {
    const creators = new Set(messages.map((msg) => msg.createdBy));
    return creators.size;
  }

  function getLastMessageTime(messages: tChatEvent[]): string {
    if (messages.length === 0) return new Date().toISOString();
    const sorted = [...messages].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return sorted[0].createdAt;
  }

  function hasAttachments(messages: tChatEvent[]): boolean {
    return messages.some(
      (msg) => msg.content?.attachments && msg.content.attachments.length > 0,
    );
  }

  const generateSummary = useCallback(
    async (conversationId: string): Promise<void> => {
      const startProccess = moment();
      const timeframeHours = 168;
      setIsSummaryLoading(true);
      setSummaryError(null);

      try {
        // Step 1: Find the most recent summary
        const previousSummary = findMostRecentSummary(leadsalesAI);

        let recentMessages: tChatEvent[] = [];
        let previousSummaryContent: string | undefined = undefined;
        let previousSummaryDate: string | undefined = undefined;

        if (previousSummary.length) {
          // Step 2: If previous summary exists, get only new messages since then
          const lastSummaryEvent = previousSummary[0];
          const lastSummaryDate = moment(lastSummaryEvent.createdAt);
          recentMessages = chatEvents.length
            ? filterMessagesAfterDate(chatEvents, lastSummaryDate)
            : [];

          previousSummaryContent = previousSummary
            .map((summary) => summary.content?.text)
            .join('\n');
          previousSummaryDate = lastSummaryEvent.createdAt;
        } else {
          // Step 3: If no previous summary, get all messages from last 7 days
          recentMessages = chatEvents.length
            ? filterRecentNonDeletedMessages(chatEvents, timeframeHours)
            : [];
        }

        if (!recentMessages.length && !previousSummaryContent) {
          const errorMessage =
            'El resumen de la conversación solo puede generarse si existen mensajes en los últimos 7 días.';
          addAlert({
            type: eAlertType.ERROR,
            message: errorMessage,
          });
          setSummaryError({
            code: 'UNKNOWN',
            message: errorMessage,
            details: { error: errorMessage },
            retryable: true,
          });
          trackEvent('front.chat.ai.summary.generate.error', {
            conversationId,
            errorCode: summaryError?.code,
            errorMessage: summaryError?.message,
          });
          return;
        }

        const conversationContent = formatMessagesForPrompt(recentMessages);
        const clientMessages = recentMessages.filter(
          (msg) => !msg.message?.fromMe,
        );
        const agentMessages = recentMessages.filter(
          (msg) => msg.message?.fromMe,
        );

        // Step 4: Generate summary with previous context if available
        const summaryContent = await ChatService.generateSummary(
          conversationId,
          {
            language: 'es',
            messages: conversationContent,
            previousSummaryContent,
            previousSummaryDate,
            metadata: {
              messageCount: recentMessages.length,
              clientMessageCount: clientMessages.length,
              agentMessageCount: agentMessages.length,
            },
          },
        );

        if (summaryContent.length) {
          const summaryEvent = {
            id: `openai_summary_${Date.now()}`,
            conversationId: conversationId,
            content: summaryContent.trim(),
            generatedAt: new Date().toISOString(),
            modelUsed: 'gpt-4o-mini',
            processingTimeMs: 0,
            tokensUsed: 1000,
            metadata: {
              messageCount: recentMessages.length,
              timeframeHours: timeframeHours,
              channels: ['chat'],
              participantCount: calculateParticipantCount(recentMessages),
              lastMessageTime: getLastMessageTime(recentMessages),
              hasAttachments: hasAttachments(recentMessages),
            },
          };

          setConversationSummary(summaryEvent);

          // Create persistent AI event for the summary
          await createSummaryAIEvent(summaryEvent);

          const endProccess = moment();

          // Track success event
          trackEvent('front.chat.ai.summary.generate.success', {
            conversationId,
            processingTimeMs: endProccess.diff(startProccess, 'milliseconds'),
            modelUsed: 'gpt-4o-mini',
            hasPreviousSummary: !!previousSummary,
          });

          // Show success notification
          addAlert({
            type: eAlertType.SUMMARY,
            message: 'Consultalo en la conversación y también en los atributos',
          });
        } else {
          const errorMessage =
            'No se pudo generar el resumen de la conversación, vuelva a internarlo, por favor!';
          addAlert({
            type: eAlertType.ERROR,
            message: errorMessage,
          });
          setSummaryError({
            code: 'UNKNOWN',
            message: errorMessage,
            details: { error: errorMessage },
            retryable: true,
          });
          trackEvent('front.chat.ai.summary.generate.error', {
            conversationId,
            errorCode: summaryError?.code,
            errorMessage: summaryError?.message,
          });
          return;
        }
      } catch (error) {
        addAlert({
          type: eAlertType.ERROR,
          message: 'No fue posible generar el resumen',
        });
        setSummaryError({
          code: 'UNKNOWN',
          message: 'No fue posible generar el resumen',
          details: { error },
          retryable: true,
        });
        trackEvent('front.chat.ai.summary.generate.error', {
          conversationId,
          errorCode: summaryError?.code,
          errorMessage: summaryError?.message,
        });
      } finally {
        setIsSummaryLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U18: Retain the existing summary action capture while stabilizing the published context.
    [
      selectedChat?.chatEvents?.events,
      currentUserId,
      addAlert,
      leadsalesAI,
      chatEvents,
    ],
  );

  const checkSummaryEligibility = useCallback(
    async (conversationId: string): Promise<void> => {
      try {
        // Always return eligible: true - eligibility restrictions removed
        if (selectedChat?.chatEvents?.events) {
          const events = selectedChat.chatEvents.events;
          const timeframeHours = 168; // 7 days

          // Filter messages from the last 7 days
          const recentMessages = filterRecentNonDeletedMessages(
            events,
            timeframeHours,
          );

          const messageCount = recentMessages.length;

          const lastMessageTime =
            recentMessages.length > 0
              ? recentMessages[recentMessages.length - 1].createdAt
              : null;

          setSummaryEligibility({
            eligible: true, // Always eligible
            messageCount,
            lastMessageTime,
            reason: undefined,
          });

          return;
        }

        // Fallback to service call if no local data
        const response = await ChatService.checkSummaryEligibility({
          conversationId,
          timeframeHours: 168,
        });
        setSummaryEligibility(response);
      } catch {
        setSummaryEligibility({
          eligible: true, // Always eligible even on error
          messageCount: 0,
          lastMessageTime: null,
          reason: undefined,
        });
      }
    },
    [selectedChat?.chatEvents?.events],
  );

  /**
   * SRE-3328: fetches the newest page of the open chat — the same
   * `ChatService.getChat` call the chat load makes — and publishes it as
   * `latestPage` for MessageContainer to swap in.
   *
   * It exists because an in-chat search can leave the thread on an anchored
   * `?around=` window three months back, and nothing else brings it back: the
   * scroll button only reaches the bottom of what is loaded. Failures toast
   * like the chat load does and leave the thread as it is.
   */
  const returnToLatest = useCallback(async (): Promise<void> => {
    const workspaceId = currentWorkspace?.id;
    const chatId = selectedChat?.id;
    const leadId = selectedChatLeadId;

    if (!workspaceId || !chatId || !leadId) return;

    try {
      const response = await ChatService.getChat({
        workspaceId,
        leadId,
        chatId,
      });

      if (openChatIdRef.current !== chatId) return;

      const page = response.data.chatEvents;

      setLatestPage((previous) => ({
        chatId,
        events: page?.events ?? [],
        cursor: page?.cursor ?? '',
        epoch: (previous?.epoch ?? 0) + 1,
      }));
    } catch (error) {
      if (isCancel(error)) return;

      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message ||
          'Ocurrió un error al obtener la información del chat',
      });
    }
  }, [currentWorkspace?.id, selectedChat?.id, selectedChatLeadId, addAlert]);

  const clearSummary = useCallback((): void => {
    setConversationSummary(null);
    setSummaryError(null);
  }, []);

  const clearSummaryError = useCallback((): void => {
    setSummaryError(null);
  }, []);

  return useMemo(() => ({
    selectedTab,
    setSelectedTab,
    selectedMessageContainer,
    setSelectedMessageContainer,
    selectedTextAreaContainer,
    setSelectedTextAreaContainer,
    selectedChat,
    setSelectedChat,
    selectedChatLeadId,
    loading,
    setLoading,
    blockTextArea,
    setBlockTextArea,
    isComposerWalletBlocked,
    setComposerWalletBlocked,
    composerSealQuoteRef,
    cursor,
    setCursor,
    rows,
    setRows,
    messages,
    setMessages,
    replyMessage,
    setReplyMessage,
    resendIds,
    setResendIds,
    selectedChatsIds,
    setSelectedChatsIds,
    selectMessageToReply,
    funnelsWithStages,
    getFunnelsWithStages,
    updateChatEventsData,
    setUpdateChatEventsData,
    chatInputContainerRef,
    textAreaContainerRef,
    showAllowTypesBanner,
    setShowAllowTypesBanner,
    followUps,
    setFollowUps,
    notes,
    setNotes,
    leadsalesAI,
    setLeadsalesAI,
    favoriteMessages,
    setFavoriteMessages,
    quickReplies,
    setQuickReplies,
    fetchQuickReplies,
    selectedNote,
    setSelectedNote,
    selectedFollowUp,
    setSelectedFollowUp,
    quickActionState,
    setQuickActionState,
    isResending,
    setIsResending,
    droppedAttachments,
    setDroppedAttachments,
    chatEvents,
    setChatEvents,
    latestPage,
    returnToLatest,
    templatePickerRequest,
    setTemplatePickerRequest,
    takeConversationRequest,
    setTakeConversationRequest,
    setNewFunnelID,
    textAreaRef,
    // Summary fields
    conversationSummary,
    isSummaryLoading,
    summaryError,
    summaryEligibility,
    generateSummary,
    checkSummaryEligibility,
    clearSummary,
    clearSummaryError,
    tagInputRef,
    groupParticipants,
    setGroupParticipants,
    autoRespondEnabled,
    setAutoRespondEnabled,
    whatsAppWebConnection,
    leadAgentPaused,
    refetchLeadAgentPaused,
  }), [
    autoRespondEnabled,
    blockTextArea,
    chatEvents,
    checkSummaryEligibility,
    clearSummary,
    clearSummaryError,
    conversationSummary,
    cursor,
    droppedAttachments,
    favoriteMessages,
    fetchQuickReplies,
    followUps,
    funnelsWithStages,
    generateSummary,
    getFunnelsWithStages,
    groupParticipants,
    isComposerWalletBlocked,
    isResending,
    isSummaryLoading,
    latestPage,
    leadAgentPaused,
    leadsalesAI,
    loading,
    messages,
    notes,
    quickActionState,
    quickReplies,
    refetchLeadAgentPaused,
    replyMessage,
    resendIds,
    returnToLatest,
    rows,
    selectMessageToReply,
    selectedChat,
    selectedChatLeadId,
    selectedChatsIds,
    selectedFollowUp,
    selectedMessageContainer,
    selectedNote,
    selectedTab,
    selectedTextAreaContainer,
    setGroupParticipants,
    showAllowTypesBanner,
    summaryEligibility,
    summaryError,
    takeConversationRequest,
    templatePickerRequest,
    updateChatEventsData,
    whatsAppWebConnection,
  ]);
}

export const useChatContext = (): tChatContext => useContext(ChatContext);
